import React, { useRef, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export interface VideoGridItem {
    id: number | string;
    title: string;
    desc: string;
    duration?: string;
    src: string;
}

interface Props {
    videos: VideoGridItem[];
}

export function VideoGrid({ videos }: Props) {
    const [activeVideo, setActiveVideo] = useState<VideoGridItem | null>(null);

    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos.map((v) => (
                    <VideoCard key={v.id} video={v} onPlay={() => setActiveVideo(v)} />
                ))}
            </div>

            <Dialog
                open={!!activeVideo}
                onClose={() => setActiveVideo(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        overflow: "hidden",
                        background: "#0E0D0B",
                        m: { xs: 1, sm: 2 },
                        maxWidth: "960px",
                    },
                }}
            >
                {activeVideo && (
                    <>
                        <IconButton
                            onClick={() => setActiveVideo(null)}
                            aria-label="Close"
                            sx={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                background: "rgba(255,255,255,0.12)",
                                backdropFilter: "blur(8px)",
                                color: "#FFFFFF",
                                zIndex: 10,
                                "&:hover": { background: "rgba(255,255,255,0.22)" },
                            }}
                            size="small"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        <DialogContent sx={{ p: 0, background: "#000" }}>
                            <video
                                key={activeVideo.id}
                                src={activeVideo.src}
                                controls
                                autoPlay
                                className="w-full aspect-video block bg-black"
                            >
                                Your browser does not support the video tag.
                            </video>
                        </DialogContent>

                        <div className="px-6 py-5 text-white">
                            <div className="flex items-start justify-between gap-4 mb-2">
                                <div
                                    className="text-xl font-bold leading-tight"
                                    style={{ fontFamily: "'CircularStdBlack', 'CircularStd', sans-serif" }}
                                >
                                    {activeVideo.title}
                                </div>
                                {activeVideo.duration && (
                                    <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold text-[#C8A97E] bg-[#C8A97E]/15 border border-[#C8A97E]/30">
                                        {activeVideo.duration}
                                    </span>
                                )}
                            </div>
                            {activeVideo.desc && (
                                <p className="text-sm text-white/70 leading-relaxed m-0">{activeVideo.desc}</p>
                            )}
                        </div>
                    </>
                )}
            </Dialog>
        </>
    );
}

function VideoCard({ video, onPlay }: { video: VideoGridItem; onPlay: () => void }) {
    const videoRef = useRef<HTMLVideoElement>(null);

    return (
        <button
            type="button"
            onClick={onPlay}
            className="group block w-full text-left bg-surface no-underline text-inherit border border-border-token rounded-xl overflow-hidden transition-all cursor-pointer hover:border-accent hover:shadow-[0_2px_12px_rgba(0,0,0,0.06)] p-0"
        >
            <div className="relative aspect-video bg-[#1C1A17] flex items-center justify-center overflow-hidden">
                <video
                    ref={videoRef}
                    src={video.src}
                    onLoadedMetadata={(e) => {
                        const el = e.currentTarget;
                        if (el) el.currentTime = 3;
                    }}
                    preload="metadata"
                    muted
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                />
                <div className="absolute inset-0 bg-black/20" />
                <div className="relative z-[2] w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all group-hover:bg-white/25 group-hover:scale-105">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#FFF">
                        <path d="M5 3l14 9-14 9V3z" />
                    </svg>
                </div>
                {video.duration && (
                    <div className="absolute bottom-2 right-2 text-[10px] font-semibold text-white bg-black/60 px-1.5 py-0.5 rounded z-[2]">
                        {video.duration}
                    </div>
                )}
            </div>
            <div className="px-3.5 py-3">
                <div className="text-sm font-semibold text-text-1 mb-1">{video.title}</div>
                {video.desc && (
                    <div className="text-xs text-muted leading-[1.4] line-clamp-2">{video.desc}</div>
                )}
            </div>
        </button>
    );
}
